import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

export default function PushSignInOption({ apiUrl, challengeToken, onApproved }) {
  const approvedCallback = useRef(onApproved);
  approvedCallback.current = onApproved;
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function sendRequest() {
    setBusy(true);
    setMessage('');
    try {
      const response = await fetch(`${apiUrl}/auth/2fa/push/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_token: challengeToken }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not request approval. Use your authenticator code.');
      setRequest(data);
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  }

  useEffect(() => {
    if (!request) return;
    let stopped = false;
    let timer;
    async function poll() {
      if (Date.now() >= new Date(request.expires_at).getTime()) {
        setMessage('Request expired. Go back and sign in again.'); setRequest(null); return;
      }
      try {
        const response = await fetch(`${apiUrl}/auth/2fa/push/status`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ challenge_token: challengeToken }),
        });
        const data = await response.json();
        if (stopped) return;
        if (response.ok && data.token) { approvedCallback.current(data.token); return; }
        if (!response.ok || ['denied', 'consumed'].includes(data.status)) {
          setMessage(data.error || (data.status === 'denied' ? 'Sign-in denied. Go back and sign in again if this was you.' : 'This request has already been used.'));
          setRequest(null); return;
        }
      } catch { /* Retry brief connection failures until the request expires. */ }
      if (!stopped) timer = setTimeout(poll, 2500);
    }
    timer = setTimeout(poll, 2500);
    return () => { stopped = true; clearTimeout(timer); };
  }, [request, apiUrl, challengeToken]);

  return <div className="border-t border-slate-200 pt-4 space-y-3 text-center">
    <p className="text-sm text-slate-600">Or approve on a device where you’re already signed in.</p>
    {request ? <div role="status" className="rounded-lg bg-slate-50 p-4 space-y-2">
      <p className="text-sm">Open the sign-in notification on your other device and enter this number:</p>
      <p className="text-3xl font-bold tracking-widest">{request.match_number}</p>
      <p className="text-sm text-slate-600">Waiting for approval… You can still use your authenticator code above.</p>
    </div> : <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={sendRequest}>
      {busy ? 'Requesting approval…' : 'Approve on another device'}
    </Button>}
    {message && <p role="status" className="text-sm text-slate-600">{message}</p>}
  </div>;
}
